/* Copyright IBM Corp. 2017 */
import { UnaryFunction } from 'rxjs/interfaces';

import { Logger } from './../../api';
import { BiFunction } from './../consumers/consumer';
import { NOOP_LOGGER } from './../logger/noop.logger';
import { isNil } from './../predicates/predicates';

/* Copyright IBM Corp. 2017 */
export type CacheCallback<V> = UnaryFunction<string, V>;
export type CacheAccessor<V> = BiFunction<string, CacheCallback<V>, V>;

interface CacheValue<V> {

    readonly value: V;

    isDirty: boolean;
}

interface CacheImpl<V> {
    [key: string]: CacheValue<V>;
}

/* Default timeout */
const DEFAULT_TIMEOUT = 30 * 1000;

/* Default count */
const DEFAULT_MAXCOUNT = 100;

/**
 * Constructs a new cache
 *
 * @param aTimeout      timeout
 * @param aMaxValues    maximum cache entries
 *
 * @return the accessor function
 */
const _createCache = <V>(aTimeout?: number, aMaxValues?: number, aLogger?: Logger): CacheAccessor<V> => {

    /*
     * Maximum number of items in the cache
     */
    const timeout = aTimeout || DEFAULT_TIMEOUT;
    const maxCount =  aMaxValues || DEFAULT_MAXCOUNT;
    const cache =  {};

    let count = 0;

    const logger = aLogger || NOOP_LOGGER;

    /**
     * Function that constructs new cache entries
     *
     * @param aKey  the key
     * @param aValue    the value
     *
     * @return a cache object
     */
    const _createCacheValue: BiFunction<string, V, CacheValue<V>> = (aKey, aValue) => {
        // return the cache value
        const cacheValue: CacheValue<V> = {
            value: aValue,
            isDirty: false
        };
        // set the eviction interval
        const intervalId = setInterval(() => {
            // check if the value is dirty
            if (cacheValue.isDirty) {
                // log this
                logger.info('Marking cache entry as dirty', aKey);
                // just update the flag
                cacheValue.isDirty = false;
            } else {
                // log this
                logger.info('Discarding cache entry', aKey);
                // delete this entry
                clearInterval(intervalId);
                delete cache[aKey];
                // decrease the count
                count--;
            }
        }, timeout);
        // insert the key
        cache[aKey] = cacheValue;
        // increase the count
        count++;
        // ok
        return cacheValue;
    }

    // returns the getter
    return (aKey: string, aCallback: CacheCallback<V>): V => {
        // check the value
        let cacheValue = cache[aKey];
        if (isNil(cacheValue)) {
            // create new value
            cacheValue = _createCacheValue(aKey, aCallback(aKey));
        } else {
            // mark as dirty
            cacheValue.isDirty = true;
        }
        // ok
        return cacheValue.value;
    }
}

export {
    _createCache as createCache
};